import { supabase } from '@/lib/supabase';
import { viewingProgressCache } from '@/utils/viewingProgressCache';
import { logoutSignalService } from './logoutSignalService';

class CacheWarmingService {
  private isWarming = false;

  async warmCacheOnAppLaunch(smartuserId: string): Promise<void> {
    if (this.isWarming) {
      console.log('📦 [CACHE-WARMING] Already warming cache, skipping...');
      return;
    }

    if (logoutSignalService.isLoggingOut) {
      console.log('📦 [CACHE-WARMING] Skipping (logout in progress)');
      return;
    }

    this.isWarming = true;
    const startTime = Date.now();

    console.log('📦 [CACHE-WARMING] 🚀 Starting background cache warming on app launch for user:', smartuserId);
    console.log('📦 [CACHE-WARMING] Platform:', require('react-native').Platform.OS);
    console.log('📦 [CACHE-WARMING] This runs in the background and does not block homepage loading');

    try {
      console.log('📦 [CACHE-WARMING] Step 1: Initializing viewingProgressCache...');
      await viewingProgressCache.initialize();
      console.log('✅ [CACHE-WARMING] Step 1: Cache initialized successfully');

      if (!supabase) {
        console.warn('⚠️ [CACHE-WARMING] Supabase not configured, skipping cache warming');
        return;
      }

      console.log('📦 [CACHE-WARMING] Step 2: Querying database for recent progress...');
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      console.log('📦 [CACHE-WARMING] Query params:', {
        smartuserId,
        sinceDate: thirtyDaysAgo.toISOString()
      });

      // Create abort controller with 10-second timeout for cache warming
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.log('⏱️ [CACHE-WARMING] Query timeout after 10000ms, aborting...');
        controller.abort();
      }, 10000);

      let recentProgress;
      let error;


      try {
        const result = await supabase
          .from('user_viewing_progress')
          .select('content_id, completion_percentage, is_completed, updated_at')
          .eq('smartuser_id', smartuserId)
          .eq('content_type', 'episode')
          .gte('updated_at', thirtyDaysAgo.toISOString())
          .order('updated_at', { ascending: false })
          .abortSignal(controller.signal);

        clearTimeout(timeoutId);
        recentProgress = result.data;
        error = result.error;
      } catch (queryError: any) {
        clearTimeout(timeoutId);

        // Handle abort errors gracefully
        if (queryError?.name === 'AbortError' || queryError?.message?.includes('abort') || queryError?.message?.includes('Abort')) {
          console.log('⏱️ [CACHE-WARMING] Query aborted due to timeout. Cache warming will be skipped.');
          return;
        }

        console.error('❌ [CACHE-WARMING] Error during query execution:', queryError);
        return;
      }

      if (error) {
        console.error('❌ [CACHE-WARMING] Error fetching recent progress:', error);
        return;
      }

      console.log('✅ [CACHE-WARMING] Step 2: Database query successful');

      if (!recentProgress || recentProgress.length === 0) {
        console.log('⚠️ [CACHE-WARMING] No recent viewing progress found for user:', smartuserId);
        return;
      }

      console.log('📦 [CACHE-WARMING] Found', recentProgress.length, 'progress records to cache');

      const cacheData = recentProgress.map((progress) => ({
        episodeId: progress.content_id.toString(),
        completionPercentage: progress.completion_percentage,
        isCompleted: progress.is_completed,
        updatedAt: progress.updated_at,
      }));

      console.log('📦 [CACHE-WARMING] Step 3: Bulk updating cache with', cacheData.length, 'entries...');
      await viewingProgressCache.bulkUpdateProgress(cacheData);
      console.log('✅ [CACHE-WARMING] Step 3: Cache bulk update complete');

      const duration = Date.now() - startTime;
      console.log(
        `✨ [CACHE-WARMING] ✅ Background cache warming completed successfully in ${duration}ms - loaded ${cacheData.length} episodes`
      );
      console.log('✨ [CACHE-WARMING] User viewing progress is now ready for instant access');

      const stats = await viewingProgressCache.getCacheStats();
      console.log('📊 [CACHE-WARMING] Final cache stats:', stats);
    } catch (error) {
      console.error('📦 [CACHE-WARMING] Error during cache warming:', error);
    } finally {
      this.isWarming = false;
    }
  }

  isCurrentlyWarming(): boolean {
    return this.isWarming;
  }
}

export const cacheWarmingService = new CacheWarmingService();
